// Vetrina del design system: mostra i nomi tecnici dei token e delle varianti come testo.
// Per questo è l'unico file escluso dalla regola `react/jsx-no-literals` (Task 10);
// i titoli e i messaggi rivolti all'utente arrivano comunque dai cataloghi.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { healthQuery } from "../api/queries";
import { AboutBox } from "../design/components/AboutBox";
import { PublicShell } from "../design/components/PublicShell";
import { Button } from "../design/components/Button";
import { Card } from "../design/components/Card";
import { Checkbox } from "../design/components/Checkbox";
import { CheckboxInput } from "../design/components/CheckboxInput";
import { Chip } from "../design/components/Chip";
import { Combobox } from "../design/components/Combobox";
import { DataTable } from "../design/components/DataTable";
import { Dialog } from "../design/components/Dialog";
import { Disclosure } from "../design/components/Disclosure";
import { Drawer } from "../design/components/Drawer";
import { IconButton } from "../design/components/IconButton";
import { LinkButton } from "../design/components/LinkButton";
import { MenuItem } from "../design/components/MenuItem";
import { NameFields } from "../design/components/NameFields";
import { OptionButton } from "../design/components/OptionButton";
import { PageHeader } from "../design/components/PageHeader";
import { PlainButton } from "../design/components/PlainButton";
import { Radio } from "../design/components/Radio";
import { RadioGroup } from "../design/components/RadioGroup";
import { Reason } from "../design/components/Reason";
import { SaveBar } from "../design/components/SaveBar";
import { Segmented } from "../design/components/Segmented";
import { Select } from "../design/components/Select";
import { ariaSortOf, SortHeader, type SortDirection } from "../design/components/SortHeader";
import { StatusPill } from "../design/components/StatusPill";
import { Table } from "../design/components/Table";
import { TextArea } from "../design/components/TextArea";
import { TextField } from "../design/components/TextField";
import { useToast } from "../design/components/Toast";
import { ToggleButton } from "../design/components/ToggleButton";
import type { Tone } from "../design/components/tone";
import { breakpoints, tokenGroups, type TokenPreview } from "../design/manifest";
import { useT } from "../i18n/locale";
import "./styleguide.css";

const buttonVariants = ["primary", "secondary", "ghost"] as const;

const tones: readonly Tone[] = ["neutral", "accent", "success", "warning", "danger"];

const roleOptions = [
  { value: "user", label: "User" },
  { value: "super-user", label: "Super user" },
  { value: "admin", label: "Admin" },
  { value: "super-admin", label: "Super admin", disabled: true },
];

// Anteprima di un token: ogni gruppo mostra il suo con lo stile del token stesso, mai con valori copiati.
function TokenPreviewBox({ kind, name }: { kind: TokenPreview; name: string }) {
  const v = `var(${name})`;
  switch (kind) {
    case "color":
      return <span className="g-styleguide__chip" /* design-eccezione: la vetrina disegna il token stesso */ style={{ background: v }} aria-hidden="true" />;
    case "text":
      return name.startsWith("--text-") ? <span /* design-eccezione: la vetrina disegna il token stesso */ style={{ fontSize: v }}>Aa</span> : <span style={{ lineHeight: v }}>Aa Bb</span>;
    case "font":
      return <span /* design-eccezione: la vetrina disegna il token stesso */ style={{ fontFamily: v }}>Aa Bb 123</span>;
    case "weight":
      return <span /* design-eccezione: la vetrina disegna il token stesso */ style={{ fontWeight: v }}>Aa Bb 123</span>;
    case "space":
      return <span className="g-styleguide__chip" /* design-eccezione: la vetrina disegna il token stesso */ style={{ width: v, height: v, background: "var(--color-accent)" }} aria-hidden="true" />;
    case "radius":
      return <span className="g-styleguide__chip" /* design-eccezione: la vetrina disegna il token stesso */ style={{ borderRadius: v, width: "var(--space-10)" }} aria-hidden="true" />;
    case "shadow":
      return <span className="g-styleguide__chip" /* design-eccezione: la vetrina disegna il token stesso */ style={{ boxShadow: v }} aria-hidden="true" />;
    case "size":
      return <span className="g-styleguide__chip" style={name.startsWith("--control") ? { height: v } : { width: `min(${v}, 100%)`, height: "var(--space-2)" }} aria-hidden="true" />;
    case "layer":
      return <span aria-hidden="true" />;
  }
}

function ServerStatus() {
  const t = useT();
  const health = useQuery(healthQuery);
  const state = health.isPending ? "pending" : health.isError ? "error" : "ok";
  return (
    <p className="g-styleguide__status" data-state={state} role="status">
      {health.isPending
        ? "—"
        : health.isError
          ? t("styleguide.serverUnavailable")
          : t("styleguide.serverVersion", { version: health.data.version })}
    </p>
  );
}

// Le finestre del sistema: una al centro (due larghezze) e un pannello a destra.
function DialogDemos() {
  const [open, setOpen] = useState<"normal" | "wide" | "drawer" | null>(null);
  const close = () => setOpen(null);
  return (
    <>
      <div className="g-styleguide__row">
        <Button onClick={() => setOpen("normal")}>Dialog</Button>
        <Button onClick={() => setOpen("wide")}>Dialog / wide</Button>
        <Button onClick={() => setOpen("drawer")}>Drawer</Button>
      </div>
      <Dialog
        open={open === "normal"}
        title="Sospendi Davide Russo"
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Annulla</Button>
            <Button variant="primary" onClick={close}>Sospendi</Button>
          </>
        }
      >
        <p>Non potrà più entrare finché non lo riattivi. I suoi Lead restano dove sono.</p>
        <TextField id="styleguide-dialog-reason" label="Motivo (facoltativo)" />
      </Dialog>
      <Dialog
        open={open === "wide"}
        title="Il WhatsApp di Luca Bianchi"
        size="wide"
        onClose={close}
        footer={
          <>
            <Button onClick={close}>Annulla</Button>
            <Button variant="primary" onClick={close}>Salva</Button>
          </>
        }
      >
        <Select id="styleguide-dialog-kind" label="Quale WhatsApp usa" value="ufficiale" onChange={() => {}} options={[{ value: "ufficiale", label: "WhatsApp ufficiale di Meta" }]} />
        <Checkbox id="styleguide-dialog-copy" label="Avvisa Luca" checked onChange={() => {}} hint="Riceve un messaggio con il nuovo numero." />
      </Dialog>
      <Drawer
        open={open === "drawer"}
        title="Il tuo account"
        onClose={close}
        footer={<Button onClick={close}>Esci</Button>}
      >
        <p>Renato Bianchi · Super admin di Gioia365</p>
        <Select id="styleguide-drawer-lang" label="Lingua" value="it" onChange={() => {}} options={[{ value: "it", label: "Italiano" }, { value: "en", label: "Inglese" }]} />
      </Drawer>
    </>
  );
}

function FieldDemos() {
  const [role, setRole] = useState("user");
  const [calls, setCalls] = useState(true);
  const [reports, setReports] = useState(false);
  return (
    <div className="g-styleguide__grid">
      <Select id="styleguide-select" label="Select" value={role} onChange={setRole} options={roleOptions} hint="Il cambio vale subito." />
      <Select id="styleguide-select-error" label="Select / error" value="" onChange={() => {}} options={[{ value: "", label: "Scegli un ruolo" }, ...roleOptions]} error="Scegli un ruolo." />
      <Select id="styleguide-select-disabled" label="Select / disabled" value="admin" onChange={() => {}} options={roleOptions} disabled />
      <div className="g-stack">
        <Checkbox id="styleguide-check" label="Checkbox" checked={calls} onChange={setCalls} hint="Vale per tutte le liste." />
        <Checkbox id="styleguide-check-off" label="Checkbox / unchecked" checked={reports} onChange={setReports} />
        <Checkbox id="styleguide-check-disabled" label="Checkbox / disabled" checked disabled onChange={() => {}} describedBy="styleguide-check-why" />
        <Reason id="styleguide-check-why">Lo decide Gioia365: non lo puoi cambiare tu.</Reason>
      </div>
    </div>
  );
}

function TableDemo() {
  return (
    <DataTable caption="Il team dell'Academy">
      <thead>
        <tr>
          <th scope="col">Persona</th>
          <th scope="col">Ruolo</th>
          <th scope="col">Stato</th>
          <th scope="col">Numero per chiamare</th>
          <th scope="col">
            <span className="g-visually-hidden">Azione</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">
            Renato Bianchi <Chip>titolare</Chip> <Chip tone="accent">sei tu</Chip>
            <span className="g-table__sub">renato@example.test</span>
          </th>
          <td>Super admin</td>
          <td><StatusPill tone="success" icon="check">Attivo</StatusPill></td>
          <td>+39 02 0000 0001</td>
          <td><Chip icon="lock">protetto</Chip></td>
        </tr>
        <tr>
          <th scope="row">
            Sara Conti
            <span className="g-table__sub">sara@example.test</span>
          </th>
          <td>Admin</td>
          <td><StatusPill tone="warning" icon="clock">Invitato</StatusPill></td>
          <td>—</td>
          <td><Button>Gestisci</Button></td>
        </tr>
        <tr>
          <th scope="row">
            Davide Russo
            <span className="g-table__sub">davide@example.test</span>
          </th>
          <td>User</td>
          <td><StatusPill tone="neutral" icon="minus">Sospeso</StatusPill></td>
          <td>+39 02 0000 0002</td>
          <td><Button>Gestisci</Button></td>
        </tr>
        <tr>
          <th scope="row">
            Elisa Fabbri
            <span className="g-table__sub">elisa@example.test</span>
          </th>
          <td>Super user</td>
          <td><StatusPill tone="danger" icon="alert">Invito scaduto</StatusPill></td>
          <td>—</td>
          <td><Button>Gestisci</Button></td>
        </tr>
      </tbody>
    </DataTable>
  );
}

// Pallini: in elenco (Impostazioni), con aiuto e una scelta spenta; a riquadri (esito della chiamata); tutto spento col motivo.
function RadioDemos() {
  const [mode, setMode] = useState<string | null>("round_robin");
  const [outcome, setOutcome] = useState<string | null>(null);
  const [kept, setKept] = useState<Record<string, "a" | "b">>({ city: "a", email: "b" });
  const [card, setCard] = useState<string | null>("meta");
  return (
    <div className="g-styleguide__grid">
      <RadioGroup
        name="styleguide-radio"
        legend="RadioGroup / list"
        value={mode}
        onChange={setMode}
        options={[
          { value: "round_robin", label: "A turno", hint: "Un Lead a testa, nell'ordine della lista." },
          { value: "owner", label: "Sempre alla stessa persona" },
          { value: "manual", label: "A mano / disabled", disabled: true },
        ]}
      />
      <RadioGroup
        name="styleguide-radio-tiles"
        legend="RadioGroup / tiles + leading"
        look="tiles"
        leading={<Button>Richiama subito</Button>}
        value={outcome}
        onChange={setOutcome}
        options={[
          { value: "answered", label: "Ha risposto" },
          { value: "no_answer", label: "Non risponde" },
          { value: "busy", label: "Occupato" },
          { value: "wrong", label: "Numero sbagliato" },
        ]}
      />
      <RadioGroup
        name="styleguide-radio-cards"
        legend="RadioGroup / cards"
        look="cards"
        value={card}
        onChange={setCard}
        options={[
          { value: "meta", label: "Canale ufficiale", aside: <Chip tone="accent">Meta</Chip>, hint: "Per i modelli approvati." },
          { value: "manual", label: "A mano", aside: <Chip>Desktop</Chip>, hint: "Il messaggio si copia e si incolla." },
        ]}
      />
      <div className="g-stack">
        <RadioGroup
          name="styleguide-radio-off"
          legend="RadioGroup / disabled"
          value="a"
          onChange={() => {}}
          disabled
          describedBy="styleguide-radio-why"
          options={[
            { value: "a", label: "Dal modulo" },
            { value: "b", label: "Dalla campagna" },
          ]}
        />
        <Reason id="styleguide-radio-why">Lo cambia solo l&apos;Admin.</Reason>
      </div>
      <Table caption="Radio / una scelta per riga, in due colonne">
        <thead>
          <tr>
            <th scope="col">Campo</th>
            <th scope="col">Resta</th>
            <th scope="col">L&apos;altro</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["city", "Città", "Milano", "Roma"],
            ["email", "Email", "a@esempio.it", "b@esempio.it"],
          ].map(([key = "", field, a, b]) => (
            <tr key={key}>
              <th scope="row">{field}</th>
              <td>
                <Radio id={`styleguide-keep-${key}-a`} name={`styleguide-keep-${key}`} value="a" label={a} checked={kept[key] === "a"} onChange={() => setKept((all) => ({ ...all, [key]: "a" }))} />
              </td>
              <td>
                <Radio id={`styleguide-keep-${key}-b`} name={`styleguide-keep-${key}`} value="b" label={b} checked={kept[key] === "b"} onChange={() => setKept((all) => ({ ...all, [key]: "b" }))} disabled={key === "email"} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function MoreFieldDemos() {
  return (
    <div className="g-styleguide__grid">
      <TextField id="styleguide-search" label="TextField / search + icon + hideLabel" hideLabel icon="search" type="search" placeholder="TextField / search + icon + hideLabel" />
      <TextField
        id="styleguide-phone"
        label="TextField / leading"
        type="tel"
        hint="Il prefisso ha la sua etichetta nascosta."
        leading={<Select id="styleguide-phone-prefix" label="Prefisso" hideLabel value="+39" onChange={() => {}} options={[{ value: "+39", label: "+39" }]} />}
      />
      <TextField id="styleguide-number" label="TextField / number" type="number" min={0} defaultValue={3} />
      <TextField id="styleguide-date" label="TextField / date" type="date" />
      <TextField id="styleguide-time" label="TextField / time" type="time" />
      <TextField id="styleguide-file" label="TextField / file" type="file" accept=".csv,text/csv" />
      <div className="g-stack">
        <TextField id="styleguide-invalid" label="TextField / invalid + describedBy" invalid describedBy="styleguide-invalid-why" defaultValue="—" />
        <p id="styleguide-invalid-why" className="g-field__error">error</p>
      </div>
    </div>
  );
}

// Pulsanti senza riquadro: solo icona, link, riga di un elenco, voce di menu.
function ButtonLikeDemos() {
  const [role, setRole] = useState("setter");
  const [pinned, setPinned] = useState(false);
  return (
    <div className="g-stack">
      <div className="g-styleguide__row">
        <IconButton label="IconButton / sm" icon="close" />
        <IconButton label="IconButton / md muted" icon="close" size="md" iconSize="md" tone="muted" />
        <IconButton label="IconButton / pressed" icon="pushpin" aria-pressed={pinned} onClick={() => setPinned((value) => !value)} />
        <IconButton label="IconButton / disabled" icon="up" disabled />
        <Button size="sm">Button / sm</Button>
        <PlainButton className="g-styleguide__cell" aria-label="PlainButton / cella di un grafico" />
        <p>
          Frase con <LinkButton>LinkButton</LinkButton> dentro. <LinkButton disabled>LinkButton / disabled</LinkButton>
        </p>
      </div>
      <div className="g-styleguide__grid">
        <ul className="g-styleguide__list">
          {[
            { value: "setter", label: "Setter", detail: "3 persone" },
            { value: "closer", label: "Closer", detail: "1 persona", aside: <Chip>di sistema</Chip> },
          ].map((item) => (
            <li key={item.value}>
              <OptionButton label={item.label} detail={item.detail} aside={item.aside} selected={role === item.value} onClick={() => setRole(item.value)} />
            </li>
          ))}
          <li>
            <OptionButton label="OptionButton / disabled" detail="busy" disabled />
          </li>
        </ul>
        <ul className="g-styleguide__list">
          <li>
            <OptionButton look="plain" selectedAs="current" selected label="Academy" detail="Super admin · aperta qui" />
          </li>
          <li>
            <OptionButton look="plain" selectedAs="current" selected={false} label="Salone" detail="Admin" />
          </li>
        </ul>
        <div role="menu" aria-label="MenuItem" className="g-styleguide__menu">
          <MenuItem label="MenuItem / con spiegazione" hint="Una riga che dice che cosa succede." />
          <MenuItem icon="pushpin" label="MenuItem / icona" />
          <MenuItem label="MenuItem / disabled" disabled />
        </div>
      </div>
    </div>
  );
}

function DisclosureDemos() {
  const [open, setOpen] = useState<string | null>("regular");
  const toggle = (key: string) => setOpen((value) => (value === key ? null : key));
  return (
    <div className="g-styleguide__grid">
      <div className="g-styleguide__panel">
        <Disclosure id="styleguide-disclosure" headingLevel={3} keepMounted open={open === "regular"} onToggle={() => toggle("regular")} summary="Disclosure / regular (h3, keepMounted)">
          <p>Il pannello resta nella pagina anche chiuso.</p>
        </Disclosure>
      </div>
      <div className="g-styleguide__panel">
        <Disclosure id="styleguide-disclosure-compact" density="compact" open={open === "compact"} onToggle={() => toggle("compact")} summary={<>Città <strong>Milano, Roma</strong></>}>
          <p>Disclosure / compact: il pannello chiuso si smonta.</p>
        </Disclosure>
      </div>
    </div>
  );
}

function SimpleTableDemo() {
  return (
    <Table caption="Table / captionHidden + busy" captionHidden busy>
      <thead>
        <tr>
          <th scope="col">Versione</th>
          <th scope="col">Quando</th>
          <th scope="col">Chi</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>3</td>
          <td>4 novembre, 10:12</td>
          <td>Sara Conti</td>
        </tr>
        <tr>
          <td>2</td>
          <td>2 novembre, 18:40</td>
          <td>Renato Bianchi</td>
        </tr>
      </tbody>
    </Table>
  );
}

const cities = [
  { id: "015146", label: "Milano", detail: "Milano, Lombardia, Italia" },
  { id: "058091", label: "Roma", detail: "Roma, Lazio, Italia" },
  { id: "087015", label: "Catania", detail: "Catania, Sicilia, Italia" },
];

// Casella combinata: qui le voci si filtrano in memoria; nell'app le cerca chi la usa (i comuni dal server).
function ComboboxDemo() {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const typed = picked ? "" : text.trim().toLowerCase();
  const options = typed.length >= 2 ? cities.filter((city) => city.label.toLowerCase().includes(typed)) : [];
  return (
    <Combobox
      id="styleguide-combobox"
      label="Combobox"
      value={text}
      onTextChange={(next) => {
        setText(next);
        setPicked(null);
      }}
      options={options}
      onPick={(option) => {
        setText(option.label);
        setPicked(option.detail ?? option.label);
      }}
      listLabel="Comuni trovati"
      status={typed.length >= 2 && options.length === 0 ? `Nessun comune per «${text.trim()}».` : null}
      hint={picked ?? "Scrivi almeno due lettere (Mil, Rom, Cat)."}
      placeholder="Combobox"
    />
  );
}

// Pillole che si accendono, intestazioni che ordinano, caselle delle righe senza etichetta scritta, etichette a fianco.
function TableControlDemos() {
  const [filters, setFilters] = useState<readonly string[]>(["calls"]);
  const [sort, setSort] = useState<{ column: string; direction: SortDirection }>({ column: "name", direction: "asc" });
  const [picked, setPicked] = useState<readonly string[]>(["a"]);
  const rows = ["a", "b", "c"];
  const toggle = (value: string) => setFilters((list) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]));
  const sortBy = (column: string) =>
    setSort((current) => ({ column, direction: current.column === column && current.direction === "asc" ? "desc" : "asc" }));
  const directionOf = (column: string) => (sort.column === column ? sort.direction : null);
  return (
    <div className="g-stack">
      <div className="g-styleguide__row" role="group" aria-label="ToggleButton">
        {[
          ["calls", "Chiamate"],
          ["notes", "Note"],
          ["whatsapp", "WhatsApp"],
        ].map(([value, text]) => (
          <ToggleButton key={value} pressed={filters.includes(value ?? "")} onClick={() => toggle(value ?? "")}>{text}</ToggleButton>
        ))}
        <ToggleButton pressed={false} disabled>ToggleButton / disabled</ToggleButton>
      </div>
      <Table caption="SortHeader + CheckboxInput" captionHidden>
        <thead>
          <tr>
            <th scope="col">
              <CheckboxInput
                label="Scegli tutte le righe"
                checked={picked.length === rows.length}
                indeterminate={picked.length > 0 && picked.length < rows.length}
                onChange={(on) => setPicked(on ? rows : [])}
              />
            </th>
            <th scope="col" aria-sort={ariaSortOf(directionOf("name"))}>
              <SortHeader label="Cognome" direction={directionOf("name")} onClick={() => sortBy("name")} />
            </th>
            <th scope="col" aria-sort={ariaSortOf(directionOf("city"))}>
              <SortHeader label="Città" direction={directionOf("city")} onClick={() => sortBy("city")} />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <td>
                <CheckboxInput
                  label={`Scegli la riga ${row}`}
                  checked={picked.includes(row)}
                  onChange={(on) => setPicked((list) => (on ? [...list, row] : list.filter((item) => item !== row)))}
                />
              </td>
              <td>{`Rossi ${row}`}</td>
              <td>Milano</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="g-styleguide__grid">
        <TextField id="styleguide-side" label="TextField / side" labelPosition="side" placeholder="ELIMINA" />
        <Select id="styleguide-select-side" label="Select / side" labelPosition="side" value="new" onChange={() => {}} options={[{ value: "new", label: "Nuovo" }, { value: "lost", label: "Perso" }]} />
        <Select id="styleguide-select-invalid" label="Select / invalid" invalid value="" onChange={() => {}} options={[{ value: "", label: "—" }]} />
        <ComboboxDemo />
      </div>
    </div>
  );
}

// Ogni tono con l'icona che di solito lo accompagna nelle tabelle.
const pillDemos = [
  { tone: "neutral", icon: "minus" },
  { tone: "accent", icon: "lock" },
  { tone: "success", icon: "check" },
  { tone: "warning", icon: "clock" },
  { tone: "danger", icon: "alert" },
] as const;

function ToastButtons() {
  const { show } = useToast();
  return (
    <div className="g-styleguide__row">
      {tones.map((tone) => (
        <Button key={tone} onClick={() => show(`Ruolo «Setter» salvato (${tone}).`, tone)}>{`toast / ${tone}`}</Button>
      ))}
    </div>
  );
}

function SegmentedDemo() {
  const [view, setView] = useState("users");
  return (
    <Segmented
      label="Segmented"
      value={view}
      onChange={setView}
      options={[
        { value: "users", label: "Utenti" },
        { value: "roles", label: "Ruoli" },
        { value: "platform", label: "Per tutte le aziende" },
      ]}
    />
  );
}

// Interruttore del tema: accende `data-theme` sulla radice, come farebbe un tema nuovo per tutta l'app.
// Vale solo finché la vetrina è aperta: uscendo si torna al tema di sempre.
function ThemeSwitch() {
  const [theme, setTheme] = useState("base");
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "base") delete root.dataset.theme;
    else root.dataset.theme = theme;
    return () => {
      delete root.dataset.theme;
    };
  }, [theme]);
  return <Segmented label="Tema" value={theme} onChange={setTheme} options={[{ value: "base", label: "Tema attuale" }, { value: "prova", label: "Tema di prova" }]} />;
}

export function StyleguidePage() {
  const t = useT();
  return (
    <PublicShell width="wide">
      <Card className="g-stack">
        <h1>{t("styleguide.title")}</h1>
        <ThemeSwitch />

        <section className="g-styleguide__section" aria-labelledby="styleguide-server">
          <h2 id="styleguide-server">{t("styleguide.serverStatus")}</h2>
          <ServerStatus />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-tokens">
          <h2 id="styleguide-tokens">{t("styleguide.tokens")}</h2>
          {tokenGroups.map((group) => (
            <div key={group.id} className="g-styleguide__section">
              <h3>{group.title}</h3>
              <ul className="g-styleguide__tokens">
                {group.tokens.map((token) => (
                  <li key={token.name} className="g-styleguide__token">
                    <TokenPreviewBox kind={group.preview} name={token.name} />
                    <code>{token.name}</code>
                    <span>{token.use}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-breakpoints">
          <h2 id="styleguide-breakpoints">{t("styleguide.breakpoints")}</h2>
          <ul className="g-styleguide__tokens">
            {breakpoints.map((point) => (
              <li key={point.px} className="g-styleguide__token">
                <code>{`${point.px} px`}</code>
                <span>{point.use}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-components">
          <h2 id="styleguide-components">{t("styleguide.components")}</h2>
          <div className="g-styleguide__row">
            {buttonVariants.map((variant) => (
              <Button key={variant} variant={variant}>{variant}</Button>
            ))}
          </div>
          <div className="g-styleguide__row">
            {buttonVariants.map((variant) => (
              <Button key={variant} variant={variant} disabled>{`${variant} / disabled`}</Button>
            ))}
          </div>
          <div className="g-stack">
            <TextField id="styleguide-field" label="TextField" placeholder="default" readOnly />
            <TextField id="styleguide-field-error" label="TextField / error" error="error" defaultValue="—" readOnly />
            <TextArea id="styleguide-textarea" label="TextArea" defaultValue={"—\n—"} readOnly />
            <NameFields id="styleguide-name" firstName={{ label: "NameFields / first", value: "—" }} lastName={{ label: "NameFields / last", value: "" }} error="error" invalid="lastName" readOnly />
          </div>
          <ButtonLikeDemos />
        </section>

        {/* Gli avvisi usano il `ToastProvider` della radice del router, come ogni altra pagina. */}
        <section className="g-styleguide__section" aria-labelledby="styleguide-dialogs">
          <h2 id="styleguide-dialogs">{t("styleguide.dialogs")}</h2>
          <DialogDemos />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-fields">
          <h2 id="styleguide-fields">{t("styleguide.fields")}</h2>
          <FieldDemos />
          <RadioDemos />
          <MoreFieldDemos />
          <DisclosureDemos />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-tables">
          <h2 id="styleguide-tables">{t("styleguide.tables")}</h2>
          <TableDemo />
          <SimpleTableDemo />
          <TableControlDemos />
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-states">
          <h2 id="styleguide-states">{t("styleguide.states")}</h2>
          <div className="g-styleguide__row">
            {pillDemos.map(({ tone, icon }) => (
              <StatusPill key={tone} tone={tone} icon={icon}>{`StatusPill / ${tone}`}</StatusPill>
            ))}
          </div>
          <div className="g-styleguide__row">
            {tones.map((tone) => (
              <Chip key={tone} tone={tone}>{`Chip / ${tone}`}</Chip>
            ))}
            <Chip icon="lock">Chip / icon</Chip>
          </div>
          <Reason>Reason / info: il motivo per cui un controllo è spento, sempre scritto.</Reason>
          <Reason tone="warning">Reason / warning: manca il fuso orario dell&apos;azienda.</Reason>
          <AboutBox title="A cosa serve">
            <p>Qui c&apos;è chi fa parte dell&apos;Academy. Da qui si invita una persona nuova e si cambia ogni cosa dalla sua riga.</p>
            <h2>Chi decide</h2>
            <p>Il Super admin, l&apos;Admin e il Super user dell&apos;azienda. Nessuno cambia chi ha un ruolo più alto del suo.</p>
          </AboutBox>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-feedback">
          <h2 id="styleguide-feedback">{t("styleguide.feedback")}</h2>
          <ToastButtons />
          <div className="g-styleguide__static">
            <SaveBar summary="2 modifiche non salvate al ruolo «Setter»: «Può chiamare» e «Vede i report»." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva il ruolo" saving={false} />
            <SaveBar summary="1 modifica da salvare." problem="Scegli almeno un giorno lavorativo." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva" saving={false} />
            <SaveBar summary="2 modifiche non salvate al ruolo «Setter»." onDiscard={() => {}} onSave={() => {}} saveLabel="Salva il ruolo" saving />
          </div>
        </section>

        <section className="g-styleguide__section" aria-labelledby="styleguide-navigation">
          <h2 id="styleguide-navigation">{t("styleguide.navigation")}</h2>
          <PageHeader eyebrow="Impostazioni · Academy" title="Team" actions={<Button variant="primary">Invita una persona</Button>} />
          <SegmentedDemo />
        </section>
      </Card>
    </PublicShell>
  );
}
